import type { AriaRole } from 'react';
import React, { useCallback } from 'react';
import type {
  ChildrenProp,
  ClassNameProp,
  VoidCallback
} from '../common-types';
import { useHover } from 'react-laag';
import { DEFAULT_HOVER_CONFIG, DEFAULT_PLACEMENT_CONFIG } from '../../util';
import { useLayerDeferedVisibility } from '../../lib';

export interface AccessibleClickableContainerProps
  extends ClassNameProp,
    ChildrenProp {
  /** Actions need to be labelled for screen readers.
   * For interactive element also undefined value can be passed
   * when we require screen reader to read only element's content */
  'aria-label'?: string;
  /** Optionally, this element can also be marked as selected. **/
  'aria-selected'?: boolean;
  /** Whether the element is enabled (and focusable) */
  disabled?: boolean;
  /** The action. */
  onClick: VoidCallback;
  /** Optional additional role, e.g. if used as child of "feed" */
  role?: AriaRole;
}

/**
 * A clickable element that can be used as e.g. a flex container.
 * It is fully accessible, including keyboard focus etc.
 *
 * Internally, this is a de-styled button, so we get excellent accessibility for free :)
 *
 * Feel free to set `display:flex` on this if you want to use it as a flex container :)
 *
 */
export function AccessibleClickableContainer({
  className,
  onClick,
  ...props
}: Readonly<AccessibleClickableContainerProps>): React.JSX.Element {
  const [isOpen, hoverProps] = useHover(DEFAULT_HOVER_CONFIG);
  const { renderLayer, triggerProps, layerProps } = useLayerDeferedVisibility({
    isOpen,
    ...DEFAULT_PLACEMENT_CONFIG
  });

  // create click handler which hides the tooltip automatically
  const onClickHidingTooltip = useCallback(() => {
    hoverProps.onMouseLeave();
    onClick?.();
  }, [onClick, hoverProps]);

  return (
    <>
      <button
        className={`ehs-accessible-click-container ${className}`}
        type="button"
        onClick={onClickHidingTooltip}
        {...triggerProps}
        {...(props['aria-label'] ? hoverProps : {})}
        {...props}
      >
        {props.children}
      </button>
      {isOpen &&
        renderLayer(
          <div {...layerProps} className="ehs-tooltip">
            {props['aria-label']}
          </div>
        )}
    </>
  );
}
